import React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { LayoutDashboard, Building2, Users, Palette, Home, Info, Calendar, MapPin,
  Landmark, ConciergeBell, Signpost, ImagePlus, LogOut, Globe, Store, Megaphone,
  Handshake, SlidersHorizontal, QrCode, CreditCard, Menu, X, Package, Activity, ScanLine } from 'lucide-react';
import { useAuth } from '../AuthContext';
import { adminApi } from '../api';

const itemCls = ({ isActive }) =>
  `flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors ${isActive ? 'bg-paelbi-brown text-white' : 'text-paelbi-ink/75 hover:bg-paelbi-bg2'}`;

export function AdminLayout({ children }) {
  const { user, isSuper, logout, tenantId, setTenantId, hasFeature } = useAuth();
  const [tenants, setTenants] = React.useState([]);
  const [open, setOpen] = React.useState(false);
  const navigate = useNavigate();

  React.useEffect(() => {
    if (!isSuper) return;
    adminApi.listTenants().then((ts) => {
      setTenants(ts);
      if (!tenantId && ts[0]) setTenantId(ts[0].id);
    });
  }, [isSuper, tenantId, setTenantId]);

  const onLogout = () => { logout(); navigate('/admin/login'); };

  return (
    <div className="min-h-screen bg-paelbi-bg lg:flex">
      {/* Mobile top bar */}
      <div className="sticky top-0 z-40 flex items-center justify-between border-b border-paelbi-line bg-white px-4 py-3 lg:hidden">
        <div className="flex items-center gap-2">
          <Store size={20} className="text-paelbi-brown" />
          <span className="font-display text-lg text-paelbi-ink">Domira</span>
        </div>
        <button aria-label="Apri menu" data-testid="admin-menu-toggle" onClick={() => setOpen(true)}
          className="inline-flex h-10 w-10 items-center justify-center rounded-btn text-paelbi-brown">
          <Menu size={24} />
        </button>
      </div>

      {/* Backdrop */}
      {open && <div className="fixed inset-0 z-40 bg-black/40 lg:hidden" onClick={() => setOpen(false)} />}

      <aside className={`fixed inset-y-0 left-0 z-50 flex w-72 max-w-[85vw] transform flex-col border-r border-paelbi-line bg-white transition-transform duration-300 lg:static lg:z-auto lg:w-64 lg:max-w-none lg:translate-x-0 ${open ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="flex items-center justify-between gap-2 border-b border-paelbi-line px-5 py-4">
          <div className="flex items-center gap-2">
            <Store size={22} className="text-paelbi-brown" />
            <div className="leading-tight">
              <p className="font-display text-lg text-paelbi-ink">Domira</p>
              <p className="text-[10px] uppercase tracking-widest text-paelbi-ink/50">Admin Console</p>
            </div>
          </div>
          <button aria-label="Chiudi menu" onClick={() => setOpen(false)} className="lg:hidden text-paelbi-ink/50"><X size={22} /></button>
        </div>

        {isSuper && (
          <div className="border-b border-paelbi-line px-4 py-3">
            <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-paelbi-ink/50">Struttura attiva</label>
            <select value={tenantId || ''} onChange={(e) => setTenantId(e.target.value)}
              className="w-full rounded-lg border border-paelbi-line bg-paelbi-bg px-2 py-1.5 text-sm">
              {tenants.map((t) => <option key={t.id} value={t.id}>{t.brand?.name} ({t.slug})</option>)}
            </select>
          </div>
        )}

        <nav className="flex flex-1 flex-col gap-1 overflow-y-auto px-3 py-4" onClick={() => setOpen(false)}>
          <NavLink to="/admin" end className={itemCls}><LayoutDashboard size={18} />Dashboard</NavLink>
          {isSuper && <NavLink to="/admin/tenants" className={itemCls}><Building2 size={18} />Strutture</NavLink>}
          {isSuper && <NavLink to="/admin/users" className={itemCls}><Users size={18} />Utenti</NavLink>}
          {isSuper && <NavLink to="/admin/catalog" className={itemCls} data-testid="nav-catalog"><Package size={18} />Catalogo Globale</NavLink>}
          {isSuper && <NavLink to="/admin/monitor" className={itemCls} data-testid="nav-monitor"><Activity size={18} />Centro di controllo</NavLink>}
          {isSuper && <NavLink to="/admin/page/domira_home" className={itemCls}><Globe size={18} />Landing Domira</NavLink>}
          <div className="my-2 border-t border-paelbi-line" />
          <NavLink to="/admin/branding" className={itemCls}><Palette size={18} />Branding & Config</NavLink>
          <NavLink to="/admin/customize" className={itemCls}><SlidersHorizontal size={18} />Personalizza struttura</NavLink>
          <NavLink to="/admin/guest-access" className={itemCls}><QrCode size={18} />Accesso ospiti</NavLink>
          <NavLink to="/admin/scans" className={itemCls} data-testid="nav-scans"><ScanLine size={18} />Scansioni QR</NavLink>
          <NavLink to="/admin/page/guest_home" className={itemCls}><Home size={18} />Home ospite</NavLink>
          <NavLink to="/admin/section/banners" className={itemCls}><Megaphone size={18} />Banner</NavLink>
          <NavLink to="/admin/section/events" className={itemCls}><Calendar size={18} />Eventi</NavLink>
          <NavLink to="/admin/section/places" className={itemCls}><MapPin size={18} />Vicino a te</NavLink>
          <NavLink to="/admin/section/museums" className={itemCls}><Landmark size={18} />Arte & Musei</NavLink>
          {hasFeature('services') && <NavLink to="/admin/section/services" className={itemCls}><ConciergeBell size={18} />Servizi</NavLink>}
          {hasFeature('partners') && <NavLink to="/admin/section/partners" className={itemCls}><Handshake size={18} />Partner</NavLink>}
          {hasFeature('experiences') && <NavLink to="/admin/section/experiences" className={itemCls}><Signpost size={18} />Esperienze</NavLink>}
          <NavLink to="/admin/page/info_bnb" className={itemCls}><Info size={18} />Info BnB</NavLink>
          <NavLink to="/admin/media" className={itemCls}><ImagePlus size={18} />Media</NavLink>
          <div className="my-2 border-t border-paelbi-line" />
          <NavLink to="/admin/subscription" className={itemCls} data-testid="nav-subscription"><CreditCard size={18} />Abbonamento</NavLink>
        </nav>

        <div className="border-t border-paelbi-line px-4 py-3">
          <p className="text-sm font-semibold text-paelbi-ink">{user?.name}</p>
          <p className="text-xs text-paelbi-ink/50">{user?.role === 'super_admin' ? 'Super Admin' : 'Admin struttura'}</p>
          <button data-testid="admin-logout-button" onClick={onLogout} className="mt-2 inline-flex items-center gap-1.5 text-sm text-paelbi-brown hover:underline"><LogOut size={15} />Esci</button>
        </div>
      </aside>

      <main className="min-w-0 flex-1 overflow-x-hidden">
        <div className="mx-auto max-w-5xl px-4 py-6 sm:px-6 lg:px-8 lg:py-8">{children}</div>
      </main>
    </div>
  );
}

export default AdminLayout;
